import React, { useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Plus, X, Save, LayoutDashboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast'; // Ensure you are using the correct toast library
import { useStarterPlan } from '../../contexts/OrganizationPlanContext';

export default function CreateTestCase({ onBack, darkMode }) {
  const navigate = useNavigate();
  const borderColor = darkMode ? 'border-gray-800/50' : 'border-gray-200';
  const cardBg = darkMode ? 'bg-[#161922]' : 'bg-white';
  const inputBg = darkMode ? 'bg-[#0f111a]' : 'bg-gray-50';
  
  // ✅ Fetch Project Data from Redux store matching your BugReport setup
  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const starterBypass = useStarterPlan();
  const canWriteTestCases =
    starterBypass ||
    (currentUser?.role !== "org_admin" && selectedProject?.user_role === "qa");

  // Form State mapping exactly to your required schema
  const [formData, setFormData] = useState({
    test_id: '',
    base_url: '',
    module: '',
    'feature/submodule': '',
    category: 'Functional', // Default selection
    type: 'Positive',       // Default selection
    'test-case-title': '',
    description: '',
    preconditions: '',
    expected_result: ''
  });

  // Test steps state (Array of strings as per schema)
  const [testSteps, setTestSteps] = useState(['']);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStepChange = (index, value) => {
    const newSteps = [...testSteps];
    newSteps[index] = value;
    setTestSteps(newSteps);
  };

  const addStep = () => setTestSteps([...testSteps, '']);

  const removeStep = (index) => {
    if (testSteps.length > 1) {
      setTestSteps(testSteps.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    if (!canWriteTestCases) return;
    // ✅ 1. Validate Project Selection
    if (!selectedProject || !selectedProject.project_id) {
      toast.error('Please select a project before creating a test case.');
      return;
    }

    // ✅ 2. Validate Required Fields
    if (!formData['test-case-title']?.trim() || !formData.test_id?.trim() || !formData.base_url?.trim() || testSteps[0].trim() === '') {
      toast.error('Please fill in all required fields and at least one test step.');
      return;
    }

    const payload = {
      project_id: selectedProject.project_id, // Safely extract the ID from Redux
      ...formData,
      test_steps: testSteps.filter(step => step.trim() !== '') // Remove empty steps
    };

    setLoading(true);
    const loadingToast = toast.loading('Saving test case...');

    try {
      await axios.post('https://api.testoraglobal.in/api/testcases/create', payload);
      toast.success('Test Case created successfully!', { id: loadingToast });
      setTimeout(() => {
        if (onBack) onBack();
        else navigate('/test-cases');
      }, 1500);
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'Server error. Please try again.',
        { id: loadingToast },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-8 pb-12 max-w-7xl mx-auto animate-in slide-in-from-right-4 duration-300">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={onBack || (() => navigate('/test-cases'))} className="p-2 hover:bg-gray-800 rounded-full transition-colors text-gray-400">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Create New Test Case</h2>
            <p className="text-gray-500 text-sm mt-1">Define comprehensive parameters and test steps manually.</p>
          </div>
        </div>

        {/* ✅ Display the active project banner to reassure the user */}
        <div className={`px-4 py-2 rounded-xl border ${borderColor} ${cardBg} flex items-center gap-3 shadow-sm`}>
            <LayoutDashboard size={16} className="text-purple-500" />
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Active Project</span>
              <span className="text-sm font-semibold">{selectedProject?.project_name || "None Selected"}</span>
            </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* MAIN DETAILS CARD */}
        <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor} space-y-6 shadow-sm`}>
          
          {/* Row 1: ID, Base URL & Title */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="col-span-1">
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Test ID *</label>
              <input name="test_id" value={formData.test_id} onChange={handleInputChange} type="text" placeholder="e.g., TC-API-001" className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm`} />
            </div>
            <div className="col-span-1">
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Base URL *</label>
              <input name="base_url" value={formData.base_url} onChange={handleInputChange} type="url" placeholder="https://www.example.com" className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm`} />
            </div>
            <div className="col-span-3">
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Test Case Title *</label>
              <input name="test-case-title" value={formData['test-case-title']} onChange={handleInputChange} type="text" placeholder="e.g., Verify Close Request Tab Action" className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm`} />
            </div>
          </div>

          {/* Row 2: Module, Feature, Category, Type */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Module *</label>
              <input name="module" value={formData.module} onChange={handleInputChange} type="text" placeholder="e.g., Authentication" className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm`} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Sub-Module</label>
              <input name="feature/submodule" value={formData['feature/submodule']} onChange={handleInputChange} type="text" placeholder="e.g., Login Screen" className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm`} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Category *</label>
              <select name="category" value={formData.category} onChange={handleInputChange} className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm cursor-pointer`}>
                <option value="Functional">Functional</option>
                <option value="UI/UX">UI/UX</option>
                <option value="Performance">Performance</option>
                <option value="Security">Security</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Type *</label>
              <select name="type" value={formData.type} onChange={handleInputChange} className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm cursor-pointer`}>
                <option value="Positive">Positive</option>
                <option value="Negative">Negative</option>
                <option value="Edge Case">Edge Case</option>
              </select>
            </div>
          </div>

          {/* Row 3: Preconditions & Description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Preconditions</label>
              <textarea name="preconditions" value={formData.preconditions} onChange={handleInputChange} rows="3" placeholder="Setup or state required before executing..." className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm resize-none`} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Description *</label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} rows="3" placeholder="Brief summary of what this test case verifies..." className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm resize-none`} />
            </div>
          </div>
        </div>

        {/* TEST STEPS & EXPECTED RESULT SECTION */}
        <div className={`p-8 rounded-3xl border ${cardBg} ${borderColor} shadow-sm`}>
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-gray-400">Execution Steps *</h3>
            <button onClick={addStep} className="bg-purple-600/10 text-purple-500 hover:bg-purple-600/20 px-4 py-2 rounded-lg flex items-center gap-2 text-xs font-bold transition-all cursor-pointer">
              <Plus size={16} /> Add Step
            </button>
          </div>

          <div className="space-y-4 mb-8">
            {testSteps.map((step, index) => (
              <div key={index} className="flex gap-4 items-start">
                <div className="mt-1 w-8 h-8 shrink-0 rounded-full bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-500 text-xs font-bold">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <input 
                    type="text" 
                    value={step}
                    onChange={(e) => handleStepChange(index, e.target.value)}
                    placeholder="Write the steps to execute this test case, one step per line" 
                    className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-purple-500 transition-all text-sm`} 
                  />
                </div>
                {testSteps.length > 1 && (
                  <button onClick={() => removeStep(index)} className="mt-1 p-2.5 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all cursor-pointer">
                    <X size={20} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="pt-6 border-gray-800/50">
             <label className="block font-bold text-gray-400 uppercase mb-2">Expected Result *</label>
             <textarea name="expected_result" value={formData.expected_result} onChange={handleInputChange} rows="2" placeholder="What should happen after all steps are executed?" className={`w-full p-3.5 rounded-xl border ${borderColor} ${inputBg} outline-none focus:border-green-500 transition-all text-sm resize-none`} />
          </div>
        </div>

        {/* FORM ACTIONS */}
        <div className="flex justify-end gap-3 pt-4">
          <button onClick={onBack || (() => navigate('/test-cases'))} className={`px-6 py-2.5 rounded-xl border ${borderColor} hover:bg-gray-800 font-bold transition-all cursor-pointer`}>
            Cancel
          </button>
          {canWriteTestCases && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold transition-all shadow-lg hover:shadow-purple-500/25 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {loading ? 'Saving...' : 'Save Test Case'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}